import { AlertTriangle, Mail, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@/components/ui/typography/Typography';
import { ConflictAccountType, type EmailConflictInfo } from '@/types/auth';
import { LOGIN_OUTLINE_BUTTON } from './loginStyles';

interface EmailConflictNoticeProps {
  conflict: EmailConflictInfo;
  /** Opens the email step with this address filled in. */
  onUseEmail: () => void;
  onDismiss: () => void;
}

/**
 * Sequence keeps one account per login method, so an email registered with Google can't sign in
 * with an email code (and the other way round). This says which method the email uses and points
 * there: the Google button (highlighted by the panel) or a one-tap "Continue with Email".
 */
export function EmailConflictNotice({
  conflict,
  onUseEmail,
  onDismiss,
}: EmailConflictNoticeProps) {
  const { t } = useTranslation('auth');
  const { email, accountType } = conflict;
  const messageKey =
    accountType === ConflictAccountType.GOOGLE
      ? 'waasAuth.emailConflict.useGoogle'
      : accountType === ConflictAccountType.EMAIL
        ? 'waasAuth.emailConflict.useEmail'
        : 'waasAuth.emailConflict.useOther';

  return (
    <div
      role='alert'
      className='flex flex-col gap-3 rounded-2xl border border-amber-400/40 bg-amber-400/10 p-4'
      data-testid='login-email-conflict'
    >
      <div className='flex items-start gap-3'>
        <AlertTriangle
          className='mt-0.5 h-4 w-4 shrink-0 text-amber-400'
          aria-hidden
        />
        <div className='flex-1'>
          <Typography variant='p3' fontWeight={600}>
            {t('waasAuth.emailConflict.title')}
          </Typography>
          <Typography variant='p4' color='muted' className='mt-1'>
            {t(messageKey, {
              email,
              accountType: t(
                `waasAuth.emailConflict.accountType.${accountType}`,
              ),
            })}
          </Typography>
        </div>
        <button
          type='button'
          onClick={onDismiss}
          aria-label={t('waasAuth.emailConflict.dismiss')}
          className='rounded p-0.5 text-muted-foreground transition-colors hover:text-white'
          data-testid='login-email-conflict-dismiss'
        >
          <X className='h-4 w-4' aria-hidden />
        </button>
      </div>
      {accountType === ConflictAccountType.EMAIL && (
        <button
          type='button'
          className={LOGIN_OUTLINE_BUTTON}
          onClick={onUseEmail}
          data-testid='login-email-conflict-use-email'
        >
          <Mail className='h-4 w-4' aria-hidden />
          <Typography
            as='span'
            variant='p3'
            fontWeight={600}
            className='text-current'
          >
            {t('waasAuth.continueWithEmail')}
          </Typography>
        </button>
      )}
    </div>
  );
}
